import { mapMessage } from '../mappers';
import { fetchWebHistoryMessages, TWebHistoryMessagesResponse } from '../repositories/chats/v3/web/history/messages';
import { TContext } from '../types/context';
import { EExceptionDomain } from '../types/exceptionDomains';
import { getGenericLikeError } from '../utils/logging/getGenericLikeError';
import { getResponseError } from '../utils/logging/getResponseError';
import { probeUserAuthentication } from './probeUserAuthentication';
import {
  EPublicApiEndpoint,
  ERequestSource,
  TRequestDiagnostics,
  TRequestSource,
  getPublicApiLogMeta,
  isMissingUserIdHeaderError,
  trackPublicApiError,
  trackPublicApiSuccess,
} from '../utils/logging/publicApiDiagnostics';

// переменная используется для отслеживания события первого сообщения
export const HISTORY_MESSAGES_REQUEST_LIMIT = 100;

interface IWebHistoryMessagesRequestParameters {
  chatId: string;
  /** Дата, после которой нужно получить сообщения  ISO-8601, пример: 2022-08-01T01:34:03.123456+00:00. **/
  createdAt?: string;
  rowVersionsHash?: string;
}

type TFetchWebHistoryMessagesParams = {
  parameters: IWebHistoryMessagesRequestParameters;
  cookies?: string;
  signal?: AbortSignal;
  causedByReplyScroll?: boolean;
  /** Источник запроса, проставляется на вызывающей стороне */
  source?: TRequestSource;
  /** Условия, в которых выполняется запрос */
  diagnostics?: TRequestDiagnostics;
};

const DEFAULT_ERROR_MESSAGE = 'Ошибка при получении сообщений чата';

export async function fetchHistoryMessages(
  { logger, httpApi, config, telemetry }: TContext,
  {
    cookies,
    parameters,
    signal,
    causedByReplyScroll = false,
    source = ERequestSource.Unknown,
    diagnostics,
  }: TFetchWebHistoryMessagesParams,
) {
  /** Код ответа нужен в catch: часть ошибок бросается уже после успешного получения ответа */
  let responseStatusCode: number | undefined;

  const requestParameters = {
    ...parameters,
    limit: HISTORY_MESSAGES_REQUEST_LIMIT,
  };

  try {
    const { response, statusCode }: TWebHistoryMessagesResponse = await fetchWebHistoryMessages({
      httpApi,
      parameters: requestParameters,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
        signal,
      },
    });

    responseStatusCode = statusCode;

    if (statusCode !== 200) {
      throw getResponseError({
        domain: EExceptionDomain.Messages,
        response,
        defaultMessage: DEFAULT_ERROR_MESSAGE,
        statusCode,
        details: { causedByReplyScroll },
      });
    }

    trackPublicApiSuccess();

    return {
      response: {
        ...response,
        messages: response.messages.map(mapMessage),
      },
      error: null,
    };
  } catch (error) {
    const exception = getGenericLikeError({
      error,
      domain: EExceptionDomain.Messages,
      message: DEFAULT_ERROR_MESSAGE,
    });

    const probe = isMissingUserIdHeaderError(error)
      ? await probeUserAuthentication({ config, httpApi, logger, telemetry })
      : null;

    logger.error(exception, {
      ...getPublicApiLogMeta({
        endpoint: EPublicApiEndpoint.HistoryMessages,
        source,
        diagnostics,
        parameters: requestParameters,
        statusCode: responseStatusCode,
        error,
        errorIndex: trackPublicApiError(),
      }),
      probeStatus: probe ? probe.status : null,
      probedUserId: probe ? probe.userId : null,
      probeStatusCode: probe ? probe.statusCode : null,
      probeErrorMessage: probe ? probe.errorMessage : null,
    });

    return { response: null, error };
  }
}
